import { LogUserActivity } from '@/apis/users/requests.ts'
import { AppDispatch } from '@/app-storage/store.ts'
import { LOG_TYPES } from '@/app/pages/family_application/constants.ts'
import SchoolYearSelector from '@/app/SchoolYearSelector.tsx'
import { Button } from '@/common/components/Button.tsx'
import PageDetails from '@/common/components/PageDetails.tsx'
import { Burger5Icon } from '@/common/icons/burgers.tsx'
import { HelpIcon, ProfileCircleIcon } from '@/common/icons/general.tsx'
import { IconType } from '@/common/icons/types.ts'
import CopyRight from '@/layouts/CopyRight.tsx'
import { useLayout } from '@/layouts/LayoutProvider.tsx'
import { PageHeaderProvider } from '@/layouts/PageHeaderProvider.tsx'
import { DesktopSidebar, MobileSidebar } from '@/layouts/Sidebar/Sidebars.tsx'
import AceScholarshipsLogo  from '@assets/ACEResized1.png'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { FamilyApplicationResp } from './apis/family_application/requests.ts'
import ContactSupportModal from './app/common/components/ContactSupportModal/ContactSupportModal.tsx'
import ForbiddenPage from './app/common/components/ForbiddenPage/ForbiddenPage.tsx'
// import LanguageSelector from './app/common/components/LanguageSelector'
import { useSignOut } from './app/common/hooks/useSignOut.tsx'
import { HelpCenterIcon } from './app/common/icons/menu.tsx'
import { LanguageManager } from './app/components/LanguageManager.tsx'
import { TitleManager } from './app/components/TitleManager.tsx'
import { TimeoutModal } from './app/pages/timeout/TimeoutModal'
import { useApplicantProgramYears } from './app/SchoolYearProvider'
import { ApplicationStatusAuthorization } from './app/services/ApplicationStatusAuthorization/ApplicationStatusAuthorization.tsx'
import './index.css'
import { UserQueries } from './queries/users/queries.ts'
import { AppRoutePaths } from './routing/route_paths.ts'
import { setLanguage } from './storage/app_settings/appSettingsSlice'
import { selectFamilyApplication } from './storage/applications/familyApplicationSlice.ts'
import StudentBalances from '@/common/components/StudentBalanceDropdown/StudentBalances.tsx'
import { CommonQueries } from '@/queries/common/queries.ts'
function App() {
  // const [showLanguageSelector, setShowLanguageSelector] = useState(true)

  const pathname = useLocation().pathname
  const { t: translate, i18n } = useTranslation()
  const StoreDispatch = useDispatch<AppDispatch>()
  const { setSidebarOpen } = useLayout()
  const application = useSelector(selectFamilyApplication)
  const SessionUserQuery = useQuery(
    UserQueries.getAuthenticatedUser(application.applicant_id)
  )

  const signOut = useSignOut()
  // const appSettings = useSelector((state: RootState) => state.AppSettings)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { applicantProgramYears } = useApplicantProgramYears()

  // useEffect(() => {
  //   const savedLanguage = appSettings.language
  //   const pathname = window.location.pathname
  //
  //   if (pathname.startsWith('/marketplace')) {
  //     if (i18n.language !== 'en') {
  //       i18n.changeLanguage('en').then()
  //     }
  //     setShowLanguageSelector(false)
  //   } else {
  //     if (savedLanguage && i18n.language != savedLanguage) {
  //       i18n.changeLanguage(savedLanguage).then()
  //     }
  //     setShowLanguageSelector(true)
  //   }
  //
  // }, [i18n.language, appSettings.language, pathname])

  useEffect(() => {
    if (i18n.language !== 'en') {
      i18n.changeLanguage('en').then(() => {})
    }
    StoreDispatch(setLanguage('en'))
  }, [i18n])

  const { data } = useQuery(
    CommonQueries.FetchHelpLink(applicantProgramYears[0]?.id, 1)
  )

  // const handleLanguageChange = (value: string) => {
  //   const path = window.location.pathname.replace(/\/+$/, '')
  //
  //   if (path.startsWith('/marketplace')) {
  //     if (i18n.language !== 'en') {
  //       i18n.changeLanguage('en').then()
  //     }
  //     return
  //   }
  //
  //   StoreDispatch(setLanguage(value))
  //
  //   if (i18n.language != value) {
  //     if (
  //       path == AppRoutePaths.home.index ||
  //       path == AppRoutePaths.messages.index
  //     ) {
  //       window.location.reload()
  //       return
  //     }
  //   }
  //
  //   i18n.changeLanguage(value).then()
  // }

  const getContactInfo = () => {
    const user = SessionUserQuery.data

    return user
      ? {
          email: user.email ?? '',
          first_name: user.first_name,
          last_name: user.last_name ?? '',
        }
      : null
  }

  function getInitials() {
    if (
      SessionUserQuery?.data?.first_name &&
      SessionUserQuery?.data?.last_name
    ) {
      return `${SessionUserQuery?.data?.first_name.slice(0, 1)}${SessionUserQuery?.data?.last_name.slice(0, 1)}`
    }
    return '' // Return empty string if no data available
  }

  if (!application) {
    return (
      <div role="status" aria-live="polite">
        {translate('loading')}
      </div>
    )
  }

  return (
    <>
      <LanguageManager />
      <TitleManager />
      <TimeoutModal />
      <DesktopSidebar />
      <MobileSidebar />
      <div className="flex flex-col h-full lg:pl-72">
        <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            aria-label="Open sidebar"
          >
            <Burger5Icon className="text-lg" aria-hidden="true" />
          </button>

          <SchoolYearSelector />
          {applicantProgramYears.length === 1 && (
            <>
              <img
                alt={translate('ace_logo')}
                src={AceScholarshipsLogo}
                className="flex lg:hidden max-h-12 w-auto object-contain"
              />
            </>
          )}
          <div className="block sm:hidden text-md font-medium">
            <PageDetails descriptionCN="hidden" />
          </div>

          <div className="flex flex-1 justify-end gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {pathname.startsWith('/marketplace') && (
                <div>
                  <StudentBalances />
                </div>
              )}
              {/*{showLanguageSelector && (*/}
              {/*  <div className="language-selector">*/}
              {/*    <LanguageSelector*/}
              {/*      defaultValue={i18n.language}*/}
              {/*      handleLanguageChange={handleLanguageChange}*/}
              {/*    />*/}
              {/*  </div>*/}
              {/*)}*/}

              {data?.link ? (
                <Link to={data.link} target="_blank" rel="noopener noreferrer">
                  <HelpIcon className="text-primary-600 hover:text-primary-700 cursor-pointer text-4xl" />
                </Link>
              ) : (
                <HelpIcon
                  className="text-primary-600 hover:text-primary-700 cursor-pointer text-4xl opacity-50 cursor-not-allowed"
                  aria-label="Help link not available"
                />
              )}

              <Menu as="div" className="relative">
                <MenuButton
                  className="-m-1.5 flex items-center p-1.5"
                  aria-label="Open user menu"
                >
                  <div
                    className="w-10 h-10 bg-primary-100 flex items-center justify-center rounded-full"
                    aria-hidden="true"
                  >
                    {getInitials()}
                  </div>

                  <span className="hidden lg:flex lg:items-center">
                    <span
                      className="ml-2 h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    >
                      <i className="ki-filled ki-down"></i>
                    </span>
                  </span>
                </MenuButton>
                <MenuItems
                  static={isDialogOpen}
                  transition
                  className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="px-4 py-3">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {getContactInfo()?.first_name}{' '}
                      {getContactInfo()?.last_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {getContactInfo()?.email}
                    </p>
                  </div>
                  <div className="py-1">
                    <MenuItem>
                      <Link
                        to={AppRoutePaths.my_account.index}
                        className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                      >
                        <ProfileCircleIcon
                          type={IconType.Outline}
                          aria-hidden="true"
                        />{' '}
                        &nbsp;{' '}
                        <span className="ml-2">{translate('profile')}</span>
                      </Link>
                    </MenuItem>
                  </div>
                  <ContactSupportModal
                    trigger={
                      <div
                        className="block w-full text-left hover:bg-gray-100 px-4 py-2  text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                        onClick={() => {
                          LogUserActivity({
                            log_type: LOG_TYPES.VIEW_PAGE,
                            suffix_text: 'Viewed the Need Help?',
                          })
                        }}
                      >
                        <HelpCenterIcon
                          className="mr-5"
                          type={IconType.Outline}
                          aria-hidden="true"
                        />
                        {translate('need_help')}
                      </div>
                    }
                    isDialogOpen={isDialogOpen}
                    setIsDialogOpen={setIsDialogOpen}
                  />
                  <div className="py-1">
                    <form
                      action="#"
                      method="POST"
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <MenuItem>
                        <div className="p-4">
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            className="w-full py-2 flex justify-center text-gray-600"
                            onClick={() => {
                              signOut()
                            }}
                          >
                            {translate('logout')}
                          </Button>
                        </div>
                      </MenuItem>
                    </form>
                  </div>
                </MenuItems>
              </Menu>
            </div>
          </div>
        </header>

        <main className="flex-grow flex flex-col">
          <ApplicationStatusAuthorization
            pathname={pathname}
            notAllowedFallbackComponent={<ForbiddenPage />}
          >
            <div className="grow px-4 py-4 lg:py-8 flex flex-col sm:px-6 sm:mx-18  xl:mx-16">
              <PageHeaderProvider>
                <Outlet context={application satisfies FamilyApplicationResp} />
              </PageHeaderProvider>
            </div>
          </ApplicationStatusAuthorization>
        </main>
        <footer className="bg-muted-white px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex text-sm items-center font-normal  gap-2 mt-auto">
            <CopyRight />
          </div>
        </footer>
      </div>
    </>
  )
}

export default App
